import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { quotesApi, type QuoteListItemDto, type ListQuotesParams, type QuoteStatus } from "@/lib/quotes";
import { QuoteStatusBadge } from "@/components/quotes/quote-status-badge";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

// ---------------------------------------------------------------------------
// Filter state
// ---------------------------------------------------------------------------

interface FilterState {
  q: string;
  status: string;
  client: ClientPickerValue | null;
}

const EMPTY_FILTERS: FilterState = {
  q: "",
  status: "",
  client: null,
};

const QUOTE_STATUSES: QuoteStatus[] = [
  "Draft",
  "Sent",
  "Approved",
  "Rejected",
  "Converted",
  "Expired",
];

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// Build API params from filter state
// ---------------------------------------------------------------------------

function buildParams(state: FilterState, pageIndex: number): ListQuotesParams {
  return {
    q: state.q || undefined,
    status: (state.status as QuoteStatus) || undefined,
    clientId: state.client?.clientId,
    page: pageIndex,
    pageSize: 50,
  };
}

// ---------------------------------------------------------------------------
// QuotesListPage
// ---------------------------------------------------------------------------

export function QuotesListPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [pageIndex, setPageIndex] = useState(0);

  const params = useMemo(() => buildParams(filters, pageIndex), [filters, pageIndex]);

  const { data, isLoading } = useQuery({
    queryKey: ["quotes", params],
    queryFn: () => quotesApi.list(params),
  });

  const visibleRows = useMemo(() => data?.items ?? [], [data]);

  function updateFilters(patch: Partial<FilterState>) {
    setFilters((f) => ({ ...f, ...patch }));
    setPageIndex(0);
  }

  const cols: DataGridColumn<QuoteListItemDto>[] = [
    {
      id: "ref",
      header: "Quote Ref",
      accessor: (r) => (
        <Link to={`/quotes/${r.quoteId}`} className="underline text-primary">
          {r.quoteRef}
        </Link>
      ),
    },
    {
      id: "description",
      header: "Description",
      accessor: (r) => r.description ?? "—",
    },
    {
      id: "client",
      header: "Client",
      accessor: (r) => r.clientName ?? "—",
    },
    {
      id: "owner",
      header: "Owner",
      accessor: (r) => r.ownerEmployeeName ?? "—",
    },
    {
      id: "total",
      header: "Total inc-GST",
      accessor: (r) => (r.totalIncGst != null ? fmt.format(r.totalIncGst) : "—"),
      align: "right",
    },
    {
      id: "status",
      header: "Status",
      accessor: (r) => <QuoteStatusBadge status={r.status} />,
    },
    {
      id: "validUntil",
      header: "Valid until",
      accessor: (r) =>
        r.validUntil ? new Date(r.validUntil).toLocaleDateString("en-AU") : "—",
    },
    {
      id: "version",
      header: "Version",
      accessor: (r) => `v${r.quoteVersionNo}`,
      align: "right",
    },
  ];

  return (
    <ListPage
      title="Quotes"
      primaryAction={
        <IntentButton onClick={() => navigate("/quotes/new")} aria-label="New quote">
          + New Quote
        </IntentButton>
      }
      filterBar={
        <div className="space-y-3">
          {/* Status filter pills */}
          <div className="flex flex-wrap gap-1">
            <span className="self-center text-xs text-muted-foreground mr-1">Status:</span>
            <button
              type="button"
              onClick={() => updateFilters({ status: "" })}
              className={`inline-flex h-7 items-center rounded-md px-2 text-xs font-medium border ${
                filters.status === ""
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-foreground border-border hover:bg-accent"
              }`}
            >
              All
            </button>
            {QUOTE_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => updateFilters({ status: s })}
                aria-pressed={filters.status === s}
                className={`inline-flex h-7 items-center rounded-md px-2 text-xs font-medium border ${
                  filters.status === s
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border hover:bg-accent"
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {/* Filter inputs */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label htmlFor="quotes-q">Search ref, description</Label>
              <Input
                id="quotes-q"
                value={filters.q}
                onChange={(e) => updateFilters({ q: e.target.value })}
                placeholder="Search…"
                aria-label="Search quotes"
              />
            </div>
            <div>
              <Label>Client</Label>
              <ClientPicker
                value={filters.client}
                onChange={(v) => updateFilters({ client: v })}
              />
            </div>
          </div>
        </div>
      }
    >
      <DataGrid
        rows={visibleRows}
        columns={cols}
        rowKey={(r) => r.quoteId}
        totalRows={data?.totalCount ?? 0}
        pageIndex={pageIndex}
        pageSize={50}
        loading={isLoading}
        emptyHeadline="No quotes match these filters."
        onPageChange={setPageIndex}
      />
    </ListPage>
  );
}
